import { useState, useCallback, useEffect, useRef } from 'react';
import { Color } from 'packages/ds/ColorPicker/types';

// EyeDropper API is not in the standard TypeScript lib — declare inline
interface EyeDropperResult {
    sRGBHex: string;
}
interface EyeDropper {
    open(options?: { signal?: AbortSignal }): Promise<EyeDropperResult>;
}
declare const EyeDropper: { new (): EyeDropper };

export type UseEyeDropperReturn = {
    pickColor: () => Promise<void>;
    isCanvasModeActive: boolean;
    onCanvasPick: (hex: string) => void;
    cancelCanvasMode: () => void;
};

export function useEyeDropper(onChange: (color: Color) => void): UseEyeDropperReturn {
    const [isCanvasModeActive, setIsCanvasModeActive] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const pickColor = useCallback(async () => {
        if ('EyeDropper' in window) {
            try {
                abortControllerRef.current?.abort();
                const controller = new AbortController();
                abortControllerRef.current = controller;
                const dropper = new EyeDropper();
                const result = await dropper.open({ signal: controller.signal });
                onChange({ color: result.sRGBHex, opacity: 100 });
            } catch {
                // User cancelled or component unmounted (AbortError) — no-op
            }
        } else {
            setIsCanvasModeActive(true);
        }
    }, [onChange]);

    const onCanvasPick = useCallback(
        (hex: string) => {
            setIsCanvasModeActive(false);
            onChange({ color: hex, opacity: 100 });
        },
        [onChange],
    );

    const cancelCanvasMode = useCallback(() => {
        setIsCanvasModeActive(false);
    }, []);

    // Abort on unmount
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    return { pickColor, isCanvasModeActive, onCanvasPick, cancelCanvasMode };
}
